/* eslint-disable arrow-body-style */
import React, { useContext, useMemo } from 'react';
import { history, shallowEqual, useIntl, useLocation, useParams, useSelector } from 'umi';
import classNames from 'classnames';
import { IStudioHeaderProps, IStudioGame } from '@/pages/home/pages/studio/type';
import { StudioContext } from '@/pages/home/pages/studio/context';
import {
  STUDIO_HUB_PATH_AI,
  STUDIO_HUB_PATH_PR,
  STUDIO_OPERATION,
} from '@/pages/home/pages/studio/constant';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { LANGUAGE_EN } from '@home/_config/config';
import {
  buildStudioGamePath,
  extractTabChildRealPath,
  getDatabrainPagePath,
  getStudioMenuPath,
  isDatabrainPage,
  listStudioHeaderHubMenuItems,
  normalizeStudioDashboardPath,
  resolveStudioHeaderHubCardImage,
} from '@/pages/home/pages/studio/utils';
import {
  getGameRouteCode,
  resolvePrHubSidebarGames,
} from '@/pages/home/pages/studio/utils/prHubSidebarGames';
import { buildSidebarHubGamePath } from '@/pages/home/pages/studio/pr/prHubSubTabs';
import type { IDashboardItem } from '@/pages/home/pages/studio/type';
import { selectValByCondition } from '@home/utils/common';
import CommonBubble from '@home/components/CommonBubble';
import { STUDIO_HOME_CARD_LABEL_BY_STUDIO_CODE } from '@/pages/home/pages/studio/_config/studioHomeCardLabelMap';
import './index.scss';

const StudioHeader = (props: IStudioHeaderProps) => {
  const { studioInfo, gamesOnly = false, } = props;
  const { locale } = useIntl();
  const location = useLocation();
  const { currentGame, currentMenu, activeTab } = useContext(StudioContext);
  const { studioId } = useParams() || {} as any;

  const frameType = useSelector((state: any) => state.studio?.frameType, shallowEqual);
  const {
    company,
    game_list: gameList = [],
    studio_info: studioData,
    studio_dashboard_list: studioDashboardList = [],
    haveSidebarGameHub,
  } = studioInfo?.data || {};

  const { icon_url: iconUrl, studio_code: studioCode, element_url: elementUrl } = studioData || {};
  /** 顶栏枢纽（与 `constant` 中 STUDIO_HUB_ROUTE_ENTRIES 的 headerHub 一致），顺序由接口 sort 决定 */
  const headerHubMenus = listStudioHeaderHubMenuItems(
    studioDashboardList as IDashboardItem[],
  );
  const showHeaderHubEntries = Boolean(
    haveSidebarGameHub || headerHubMenus.length,
  );

  const {
    name: companyName,
    national_flag: nationalFlag,
    location_country: locationCountry,
    location_city: locationCity,
  } = company || {};

  const name = studioCode === 'studiodemo' ? 'Studio Hub Demo' : companyName;
  const addrInfo: string = [locationCity, locationCountry]?.filter((item) => item)?.join(', ') || '';

  /** Home 入口底部文案：仅 {@link STUDIO_HOME_CARD_LABEL_BY_STUDIO_CODE}，未配置则不展示 */
  const studioHomeCardLabel = useMemo(() => {
    const codeKey = studioCode?.trim().toLowerCase() ?? '';
    const mapped = codeKey
      ? STUDIO_HOME_CARD_LABEL_BY_STUDIO_CODE[codeKey]
      : undefined;
    if (!mapped) {
      return '';
    }
    return selectValByCondition(
      locale === LANGUAGE_EN,
      mapped.nameEn,
      mapped.nameCn,
    );
  }, [studioCode, locale]);

  const isStudioHomePath = Boolean(
    frameType === 'studio' && new RegExp('^/studio/[^/]+/home(?:/|$)').test(location.pathname),
  );
  const isHeaderHubActivePath = headerHubMenus.some((menu) => {
    const seg = normalizeStudioDashboardPath(menu.path).replace(/^\//, '');
    return (
      frameType === 'studio' &&
      new RegExp(`^/studio/[^/]+/${seg}(?:/|$)`).test(location.pathname)
    );
  });

  const handleSidebarHubNavigate = (menu: IDashboardItem) => {
    const hubRoot = menu.path || '';
    const hubNorm = normalizeStudioDashboardPath(hubRoot);
    if (isDatabrainPage(menu)) {
      history.push(
        `/studio/${studioId}/${getDatabrainPagePath(menu)}`,
      );
      return;
    }
    if (hubNorm === normalizeStudioDashboardPath(STUDIO_HUB_PATH_AI)) {
      history.push(`/studio/${studioId}${STUDIO_HUB_PATH_AI}`);
      return;
    }
    if (hubNorm !== normalizeStudioDashboardPath(STUDIO_HUB_PATH_PR)) {
      history.push(`/studio/${studioId}${hubRoot}`);
      return;
    }
    const prGames = resolvePrHubSidebarGames(studioInfo?.data as any);
    const first = prGames.find((g) => Boolean(getGameRouteCode(g)));
    const firstCode = first ? getGameRouteCode(first) : '';
    history.push(
      firstCode
        ? buildSidebarHubGamePath(studioId ?? '', hubRoot, firstCode)
        : `/studio/${studioId}${hubRoot}`,
    );
  };

  const handleClickGame = (type: string, game?: IStudioGame, dashboardList?: Array<any>) => {
    if (type === 'studio') {
      history.push(`/studio/${studioId}/home`);
    } else {
      const rawPath = getStudioMenuPath(currentMenu);
      // display_mode=tab 容器：rawPath 格式为 {nameSlug}/{childPath}，提取 childPath 用于跨游戏路径保持匹配
      // extractTabChildRealPath 返回空字符串说明不是 tab 容器路径
      const childPath = extractTabChildRealPath(rawPath);
      const isTabPath = currentMenu?.display_mode === 'tab';
      const matchPath = isTabPath ? childPath : rawPath;
      const code = game?.game_code || game?.unified_edition_id;
      const hasResearchList = (game?.dashboard_research_list?.length ?? 0) > 0;
      const targetTab = hasResearchList ? activeTab : STUDIO_OPERATION;
      if (dashboardList?.length === 0) {
        history.push(
          buildStudioGamePath(
            game?.studio_code,
            code,
            '',
            targetTab,
            hasResearchList,
          ),
        );
      } else {
        // 目标游戏菜单列表中，提取每项的"可比较路径"（tab 容器取 childPath，普通菜单取 path）
        const dashboardListPathArr = dashboardList?.map(item => {
          if (item?.display_mode === 'tab') return extractTabChildRealPath(getStudioMenuPath(item));
          return getStudioMenuPath(item);
        });
        const isHaveLastGamePath = dashboardListPathArr?.includes(matchPath);
        if (isHaveLastGamePath) {
          const targetDashboard = dashboardList?.find((item) => {
            const cp = item?.display_mode === 'tab'
              ? extractTabChildRealPath(getStudioMenuPath(item))
              : getStudioMenuPath(item);
            return cp === matchPath;
          });
          const targetPath = targetDashboard ? getStudioMenuPath(targetDashboard) : matchPath;
          history.push(
            buildStudioGamePath(
              game?.studio_code,
              code,
              targetPath,
              targetTab,
              hasResearchList,
            ),
          );
        } else {
          history.push(
            buildStudioGamePath(
              game?.studio_code,
              code,
              '',
              targetTab,
              hasResearchList,
            ),
          );
        }
      }
    }
  };

  const showHomeEntry = Boolean(studioInfo?.data?.haveHome) && !gamesOnly;
  const showHubEntries = showHeaderHubEntries && !gamesOnly;
  const suppressGameActiveOnCasualHome = Boolean(
    gamesOnly &&
      new RegExp('^/studio/[^/]+/home(?:/|$)').test(location.pathname),
  );

  return <div className='dbh-studio-header-container'>
    {!gamesOnly && (
      <div className='dbh-studio-header'>
        <div className='dbh-studio-header-context'>
          <div className="dbh-studio-header-title">{name}</div>
          <div className="dbh-studio-header-flex">
            <div className="dbh-studio-header-country">
              <img className='dbh-studio-header-country-img' src={getCountryFlag(nationalFlag?.toLowerCase())} />
              {addrInfo}
            </div>
          </div>
        </div>
      </div>
    )}

    {
      (gameList?.length > 0 || showHomeEntry || showHubEntries) && <div className='dbh-studio-header-imglist'>
        <div className='dbh-studio-lmg'>
          {
            showHomeEntry && <div
              className={classNames(
                'dbh-studio-lmg-item',
                'dbh-studio-lmg-studio',
                'dbh-studio-lmg-item--pr-hub',
                { active: isStudioHomePath },
              )}
              onClick={() => handleClickGame('studio')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClickGame('studio');
                }
              }}
            >
              <img className='dbh-studio-lmg-item-img' src={iconUrl} alt="" />
              {studioHomeCardLabel ? (
                <span className="dbh-studio-lmg-item-text">
                  <span className="dbh-studio-lmg-item-text-name">
                    {studioHomeCardLabel}
                  </span>
                </span>
              ) : null}
            </div>
          }
          {
            showHubEntries && headerHubMenus.map((hubMenu) => {
              const hubRootNorm = normalizeStudioDashboardPath(hubMenu.path);
              const hubImg = resolveStudioHeaderHubCardImage(
                hubMenu.path,
                elementUrl,
              );
              const seg = hubRootNorm.replace(/^\//, '');
              const isHubActive =
                frameType === 'studio' &&
                new RegExp(`^/studio/[^/]+/${seg}(?:/|$)`).test(location.pathname);
              return (
                <div
                  key={hubMenu.u_id || hubMenu.path || seg}
                  className={classNames('dbh-studio-lmg-item', 'dbh-studio-lmg-item--pr-hub', {
                    active: isHubActive,
                  })}
                  onClick={() => handleSidebarHubNavigate(hubMenu)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSidebarHubNavigate(hubMenu);
                    }
                  }}
                >
                  {hubImg ? (
                    <img className="dbh-studio-lmg-item-img" src={hubImg} alt="" />
                  ) : null}
                  <span className="dbh-studio-lmg-item-text">
                    <span className="dbh-studio-lmg-item-text-name">
                      {selectValByCondition(
                        locale === LANGUAGE_EN,
                        hubMenu.name_en,
                        hubMenu.name_cn,
                      )?.replaceAll(' ', ' ')}
                    </span>
                  </span>
                </div>
              );
            })
          }
          {
            gameList?.map((game) => {
              const { game_code: gameCode, unified_edition_id: id, cover, game_name: nameEn, game_name_cn: nameCn, dashboard_list: dashboardList, game_tags: gameTags } = game || {};
              const name = selectValByCondition(locale === LANGUAGE_EN, nameEn, nameCn);
              const isActive = currentGame?.game_code ? currentGame?.game_code === gameCode : currentGame?.unified_edition_id === id;
              const isWIP = (gameTags || []).map(tag => tag.trim().toLowerCase()).includes('wip');
              const handleGameClick = (e: { stopPropagation: () => void; }) => {
                if (isWIP) {
                  e.stopPropagation();
                } else {
                  handleClickGame('game', game, dashboardList);
                }
              };
              const gameItem = (
                <div
                  className={classNames('dbh-studio-lmg-item', { 'active': isActive && !isStudioHomePath && !isHeaderHubActivePath && !suppressGameActiveOnCasualHome && !isWIP, 'no-hover': isWIP })}
                  key={gameCode || id}
                  onClick={handleGameClick}
                >
                  <img className='dbh-studio-lmg-item-img' src={cover} />
                  <span className='dbh-studio-lmg-item-text'>
                    <span className='dbh-studio-lmg-item-text-name'>{name?.replaceAll(' ', ' ')}</span>
                  </span>
                  {isWIP && <span className='dbh-studio-lmg-item-wip'>{'WIP'}</span>}
                </div>
              );
              return isWIP ? (
                <CommonBubble content={selectValByCondition(locale === LANGUAGE_EN, 'Work In Progress', '敬请期待')}>
                  {gameItem}
                </CommonBubble>
              ) : (
                gameItem
              );
            })
          }
        </div>
      </div>
    }

  </div>;
};

export default StudioHeader;
